import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  ChangeDetectorRef,
  OnDestroy,
  AfterViewInit,
  ViewChild, TemplateRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { PaginatorModule } from 'primeng/paginator';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { CheckboxModule } from 'primeng/checkbox';
import { CardModule } from 'primeng/card';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { FileStorageItemService } from '@6grain/masofa-monolith-api-client/common/api/fileStorageItem.service';
import { AdminFileStorageService } from '@6grain/masofa-monolith-api-client/common/api/adminFileStorage.service';
import { FileStorageItemBaseGetQuery } from '@6grain/masofa-monolith-api-client/common/model/fileStorageItemBaseGetQuery';
import { FileStorageItem } from '@6grain/masofa-monolith-api-client/common/model/fileStorageItem';
import { FieldFilter } from '@6grain/masofa-monolith-api-client/common/model/fieldFilter';
import { FilterOperator } from '@6grain/masofa-monolith-api-client/common/model/filterOperator';
import { SortType } from '@6grain/masofa-monolith-api-client/common/model/sortType';
import {of, Subject, takeUntil} from 'rxjs';
import {DataTableComponent, TableColumn, TableConfig} from '@components/data-table/data-table.component';
import {ListPageWrapper} from '@app/wrappers/pages/list-page-wrapper/list-page-wrapper';
import {UserService, ProfileInfoViewModel} from '@6grain/masofa-monolith-api-client/identity';
import {userNames} from '@app/utils/_msf';
import {Filters} from '@components/views/filters/filters';
import {IMsfFormBuilder} from '@components/form-builder/form-builder';
import {catchError} from 'rxjs/operators';
import {MessageService} from 'primeng/api';
// @ts-ignore

interface FileStorageItemExtended extends FileStorageItem {
  selected?: boolean;
}

@Component({
  selector: 'app-file-storage-management-page',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ButtonModule,
    InputTextModule,
    TableModule,
    PaginatorModule,
    FormsModule,
    RouterLink,
    CheckboxModule,
    CardModule,
    DatePickerModule,
    SelectModule,
    DialogModule,
    TooltipModule,
    ToastModule,
    DataTableComponent,
    ListPageWrapper,
  ],
  templateUrl: './file-storage-management-page.html',
  styleUrl: './file-storage-management-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileStorageManagementPageComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('actionDownloadTemplate') actionDownloadTemplate!: TemplateRef<any>;

  @ViewChild('userTemplate') userTemplate!: TemplateRef<any>;

  @ViewChild('dateTemplate') dateTemplate!: TemplateRef<any>;

  @ViewChild('idTemplate') idTemplate!: TemplateRef<any>;

  files: FileStorageItemExtended[] = [];
  buckets: string[] = [];
  filteredBuckets: string[] = [];
  selectedBucket: string | null = null;
  bucketFilter: string = '';
  showFileInfoModal = false;
  selectedFile: FileStorageItem | null = null;
  selectAll: boolean = false;

  pageSize: number = 10;
  currentPage: number = 1;
  totalRecords: number = 0;

  pageSizeOptions = [
    {label: '10', value: 10},
    {label: '25', value: 25},
    {label: '50', value: 50},
    {label: '100', value: 100}
  ];

  filters: any = {};
  showFilters: boolean = false;

  tableColumns: TableColumn[] = [];
  tableConfig: TableConfig = {
    selectable: false,
    paginator: true,
    rows: 10,
    rowsPerPageOptions: [10, 25, 50, 100],
    loading: false,
    emptyMessage: 'No data found',
  };
  // Filter properties

  loading: boolean = false;
  private destroy$ = new Subject<void>();
  requestFilters: IMsfFormBuilder[] = []
  users: ProfileInfoViewModel[] = [];

  constructor(
    private cdr: ChangeDetectorRef,
    private fileStorageService: FileStorageItemService,
    private adminFileStorageService: AdminFileStorageService,
    private userService: UserService,
    private translate: TranslateService,
    private messageService: MessageService
  ) {
  }

  ngOnInit(): void {
    this.loadBuckets();
    this.loadFileStorageData();
    this.requestFilters = [
      {
        name: 'id',
        title: this.translate.instant('File ID'),
        type: 'input',
        placeholder: this.translate.instant('Enter file ID')
      }
    ]

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    this.tableColumns = [
      {
        field: 'id',
        header: this.translate.instant('ID'),
        type: 'custom',
        customTemplate: this.idTemplate,
        sortable: false
      },
      {
        field: 'createAt',
        header: this.translate.instant('Created'),
        type: 'custom',
        customTemplate: this.dateTemplate,
        sortable: false
      },
      {
        field: '',
        header: this.translate.instant('Created By'),
        type:'custom',
        customTemplate: this.userTemplate,
        sortable: false,
      },
      {field: 'ownerId', header: this.translate.instant('Owner ID'), sortable: false},
      {field: 'ownerTypeFullName', header: this.translate.instant('Owner Type'), sortable: false},
      {field: 'fileStoragePath', header: this.translate.instant('File Path'), sortable: false},
      {field: 'fileStorageBacket', header: this.translate.instant('Storage Bucket'), sortable: false},
      {
        field: '',
        header: this.translate.instant('Actions'),
        type: 'custom',
        customTemplate: this.actionDownloadTemplate
      }
    ];
  }

  private loadBuckets(): void {
    this.adminFileStorageService.commonAdminFileStorageGetBucketsGet()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: buckets => {
          this.buckets = buckets || [];
          this.filteredBuckets = this.buckets.slice();
          if (!this.selectedBucket && this.buckets.length) {
            this.selectedBucket = this.buckets[0];
            this.applyBucket();
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.buckets = [];
          this.filteredBuckets = [];
          this.cdr.detectChanges();
        }
      });
  }

  loadFileStorageData(): void {
    this.loading = true;

    const query: FileStorageItemBaseGetQuery = {
      take: this.pageSize,
      offset: (this.currentPage - 1) * this.pageSize, // Convert to 0-based for API
      sort: SortType.NUMBER_1,
      filters: this.buildFilters()
    };

    this.loadTotalCount().then(() => {
      this.loadUsers().then(() => {
        // If a bucket is selected, use AdminFileStorage to list files by bucket
        if (this.selectedBucket) {
          this.adminFileStorageService.commonAdminFileStorageGetFilesByBucketGet(this.selectedBucket)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (items: FileStorageItem[]) => {
                this.files = items.map(item => ({ ...item, selected: false }));
                this.totalRecords = this.files.length;
                this.loading = false;
                this.cdr.detectChanges();
              },
              error: (error) => {
                console.error('Error loading files by bucket:', error);
                this.files = [];
                this.totalRecords = 0;
                this.loading = false;
                this.cdr.detectChanges();
              }
            });
        } else {
          // Fallback to general listing by query
          this.fileStorageService.commonFileStorageItemGetByQueryPost(query)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (items: FileStorageItem[]) => {
                this.files = items.map(item => ({ ...item, selected: false }));
                if (this.files.length < this.pageSize) {
                  this.totalRecords = (this.currentPage - 1) * this.pageSize + this.files.length;
                } else {
                  this.totalRecords = this.currentPage * this.pageSize + 1;
                }
                this.loading = false;
                this.cdr.detectChanges();
              },
              error: (error) => {
                console.error('Error loading file storage data:', error);
                this.files = [];
                this.totalRecords = 0;
                this.loading = false;
                this.cdr.detectChanges();
              }
            });
        }
      });
    });
  }

  private async loadUsers(): Promise<void> {
    try {
      const users = await this.userService.identityUserGetByQueryPost({
        take: 1000,
        offset: 0
      }).pipe(catchError(() => of([]))).toPromise();

      this.users = users || [];
    } catch (error) {
      console.error('Error loading users:', error);
      this.users = [];
    }
  }

  private async loadTotalCount(): Promise<void> {
    this.totalRecords = this.pageSize * 20;
  }
  private buildFilters(): FieldFilter[] {
    const filters: FieldFilter[] = [];

    if (this.filters.id && this.filters.id.trim()) {
      filters.push({
        filterField: 'id',
        filterValue: this.filters.id.trim(),
        filterOperator: FilterOperator.NUMBER_0
      });
    }

    return filters;
  }

  applyFilters(): void {
    this.currentPage = 1; // Reset to first page when applying filters
    this.loadFileStorageData();
  }

  clearFilters(): void {
    this.currentPage = 1; // Reset to first page when clearing filters
    this.filters = {};
    this.loadFileStorageData();
  }

  onPageChange(event: any): void {
    this.currentPage = event.page + 1; // PrimeNG paginator is 0-based, convert to 1-based
    this.pageSize = event.rows;
    this.loadFileStorageData();
  }

  onPageSizeChange(event: any): void {
    this.pageSize = event.value;
    this.currentPage = 1; // Reset to first page when changing page size
    this.loadFileStorageData();
  }

  formatDate(dateString: string | undefined): string {
    if (!dateString) return '-';

    try {
      const date = new Date(dateString);
      const pad = (n: number) => n < 10 ? '0' + n : n;
      return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    } catch {
      return dateString;
    }
  }

  onSelectAllChange(event: any): void {
    this.files.forEach(file => {
      file.selected = this.selectAll;
    });
  }

  onRowClick(file: FileStorageItemExtended): void {
    if (!file?.id) return;
    this.fileStorageService.commonFileStorageItemGetByIdIdGet(file.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (info) => {
          this.selectedFile = info;
          this.showFileInfoModal = true;
          this.cdr.detectChanges();
        },
        error: () => {}
      });
  }

  getUser(createUser: string): any {
    return this.users.find(user => user.id === createUser) || null;
  }

  copyFileId(fileId: string | undefined): void {
    if (!fileId) {
      console.error('No file ID provided');
      return;
    }

    // Copy to clipboard
    navigator.clipboard.writeText(fileId).then(() => {
      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('Success'),
        detail: this.translate.instant('File ID copied to clipboard')
      });
    }).catch(err => {
      console.error('Failed to copy file ID:', err);
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('Error'),
        detail: this.translate.instant('Failed to copy file ID')
      });
    });
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  exportFiles(): void {
  }

  deleteSelected(): void {
    const selectedCount = this.files.filter(f => f.selected).length;
    if (selectedCount > 0) {
    }
  }

  downloadFileById(fileId: string | undefined): void {
    if (!fileId) {
      console.error('No file ID provided');
      return;
    }

    // Use existing FileStorageItemController.GetFile endpoint
    const url = `/common/FileStorageItem/GetFile/${fileId}`;
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.click();
  }

  onRowSelect($event: any) {

  }

  onRowUnselect($event: any) {

  }

  onSort($event: any) {

  }

  onFilter($event: any) {

  }

  applyBucket(): void {
    this.currentPage = 1;
    this.loadFileStorageData();
  }

  applyBucketFilter(): void {
    const q = (this.bucketFilter || '').toLowerCase();
    this.filteredBuckets = !q ? this.buckets.slice() : this.buckets.filter(b => b.toLowerCase().includes(q));
  }

  selectBucket(b: string): void {
    if (this.selectedBucket === b) return;
    this.selectedBucket = b;
    this.applyBucket();
  }

  refreshBuckets(): void {
    this.loadBuckets();
  }

  protected readonly userNames = userNames;

}
